import { createContext, useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import api from '../utils/api'
import toast from 'react-hot-toast'
import { deleteStoredPushToken } from '../utils/pushClient'
import { hapticSuccess } from '../utils/haptics'
import useSalonStore from '../store/useSalonStore'
import { signOutGoogleNative } from '../utils/nativeSocialLogin'
import { clearSessionStorage } from '../utils/session'

// Persistent sessions: always write auth to localStorage. Previously we
// branched between localStorage (remember me checked) and sessionStorage
// (unchecked), but the default-unchecked UX meant every tab close
// silently logged users out — which is what users were complaining
// about. Paired with the 30-day server-side JWT expiry this gives a
// "stay signed in" default that mirrors what every other consumer app
// does. XSS trade-off noted: localStorage tokens are readable by any
// injected script, so the app still needs its existing CSP + sanitizer
// posture to make that a non-issue. Moving to httpOnly cookies is the
// stronger long-term fix; out of scope for this change.
const TOKEN_KEY = 'token'
const USER_KEY = 'user'

// Best-effort cleanup of the pre-change session store. Anyone who was
// logged in via the old rememberMe=false path has a stale token sitting
// in sessionStorage; wipe it once at module load so it can't
// resurrect itself later via any lingering fallback read.
try { sessionStorage.removeItem(TOKEN_KEY) } catch { /* noop */ }
try { sessionStorage.removeItem(USER_KEY) } catch { /* noop */ }

const readToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}

const writeSession = (token, userData) => {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
  } catch { /* quota / private mode — silently fall back to in-memory */ }
}

// Tears down token + user AND the impersonation keys (impersonating,
// adminToken). Delegates to the shared utils/session helper so the manual-
// logout path here and the 401-expiry path in utils/api.js can't drift —
// that drift is what left the red "Ești logat ca…" banner stuck on /login
// after an impersonated token expired mid-use.
const clearSession = () => {
  clearSessionStorage()
}

export const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Drop all in-memory account state — the per-salon Zustand store AND the whole
  // React Query cache (its keys are user-agnostic: qk.mySalon=['salon','me'],
  // notifications, bookings…). MUST run on EVERY local session teardown, because
  // the native app has NO page reload between sessions, so a stale cache would
  // otherwise bleed the previous account's data into the next login (observed: a
  // demo owner saw a Bubble Pet specialist's dashboard). Shared by explicit
  // logout() AND the 401-expiry path in checkAuth() so the two can't drift.
  const resetAccountState = () => {
    useSalonStore.getState().reset()
    queryClient.clear()
  }

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    const token = readToken()
    if (!token) {
      setLoading(false)
      return
    }
    try {
      const { data } = await api.get('/auth/profile')
      setUser(data.data || data.user || data)
    } catch (err) {
      // 401 = token rejected by the server (expired or revoked). Clear
      // everything and force re-login; api.js's response interceptor
      // will handle the /login redirect if we're on a protected route.
      if (err.response?.status === 401) {
        clearSession()
        setUser(null)
        // Same teardown as an explicit logout — otherwise a token that expires
        // mid-session leaves the previous account's Zustand store + React Query
        // cache in memory, and the next login (no reload on native) is served
        // stale data. Matches logout()'s cleanup exactly.
        resetAccountState()
      } else {
        // Network / 5xx. Don't nuke the session — optimistically
        // rehydrate `user` from the locally cached payload so the UI
        // stays usable offline. The next successful profile call
        // replaces it with the authoritative copy.
        try {
          const stored = localStorage.getItem(USER_KEY)
          if (stored) setUser(JSON.parse(stored))
        } catch { setUser(null) }
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password })
      const token = data.token || data.data?.token
      const userData = data.user || data.data?.user || data.data
      writeSession(token, userData)
      setUser(userData)
      toast.success('Autentificare reușită!')
      hapticSuccess()
      return userData
    } catch (error) {
      const message = error.response?.data?.message || 'Eroare la autentificare'
      toast.error(message)
      throw error
    }
  }

  const register = async (registerData) => {
    try {
      const { data } = await api.post('/auth/register', registerData)
      // If verification is required, return the data without logging in
      if (data.requiresVerification) {
        toast.success('Verifică-ți email-ul pentru codul de activare!')
        return data
      }
      const token = data.token || data.data?.token
      const userData = data.user || data.data?.user || data.data
      if (token) {
        writeSession(token, userData)
        setUser(userData)
      }
      toast.success('Cont creat cu succes!')
      hapticSuccess()
      return userData
    } catch (error) {
      const message = error.response?.data?.message || 'Eroare la înregistrare'
      toast.error(message)
      throw error
    }
  }

  const loginUser = (token, userData) => {
    writeSession(token, userData)
    setUser(userData)
  }

  const loginWithGoogle = async (credential, role) => {
    try {
      const { data } = await api.post('/auth/google', { credential, role })
      const token = data.token || data.data?.token
      const userData = data.user || data.data?.user || data.data
      writeSession(token, userData)
      setUser(userData)
      toast.success('Autentificare cu Google reușită!')
      hapticSuccess()
      return userData
    } catch (error) {
      const message = error.response?.data?.message || 'Eroare la autentificarea cu Google'
      toast.error(message)
      throw error
    }
  }

  // Sign in with Apple (native). identityToken is verified server-side; fullName
  // is forwarded because Apple only sends the name on the FIRST authorization.
  // Mirrors loginWithGoogle — same session write + redirect contract.
  const loginWithApple = async (identityToken, fullName, role, authorizationCode) => {
    try {
      const { data } = await api.post('/auth/apple', { identityToken, fullName, role, authorizationCode })
      const token = data.token || data.data?.token
      const userData = data.user || data.data?.user || data.data
      writeSession(token, userData)
      setUser(userData)
      toast.success('Autentificare cu Apple reușită!')
      hapticSuccess()
      return userData
    } catch (error) {
      const message = error.response?.data?.message || 'Eroare la autentificarea cu Apple'
      toast.error(message)
      throw error
    }
  }

  const logout = () => {
    // Disassociate this device's push token from the user first — it reads the
    // auth JWT synchronously, which clearSession() wipes on the next line.
    deleteStoredPushToken()
    clearSession()
    // Native: also sign the Google SDK out so the next "Continue with Google"
    // shows the account picker instead of silently re-using the last account.
    // Fire-and-forget — no-op on web, never blocks the local logout.
    signOutGoogleNative()
    // Belt-and-suspenders for the pre-change data path. A returning
    // user still on an old build in another tab might have pushed
    // something here — clear it so the next mount starts truly empty.
    try {
      sessionStorage.removeItem(TOKEN_KEY)
      sessionStorage.removeItem(USER_KEY)
    } catch { /* noop */ }
    setUser(null)
    // Wipe the per-salon Zustand store + the whole React Query cache so a second
    // user logging in on the same tab (or native session) never briefly sees the
    // previous account's salon / appointments / clients. See resetAccountState.
    resetAccountState()
    toast.success('Te-ai deconectat cu succes!')
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginUser, loginWithGoogle, loginWithApple, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  )
}
